import { FC, useState } from "react";
import { Field, useField, useForm } from "react-final-form";

import { Add, Close, Info } from "@livechat/design-system-icons";
import {
  AutoComplete,
  Button,
  DesignToken,
  FormField,
  Icon,
  SpacingToken,
  Tag,
  Tooltip,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useDebouncedAddresses } from "hooks/useDebounceGetAddresses";
import { withVar } from "themes/helpers";

import { validatePeopleInTheLoopEmail } from "./formUtils";

interface IProps extends IWithBreakpointsProps {
  requesterEmail: string | undefined;
}

const PeopleInTheLoopTooltip: FC = () => (
  <Tooltip
    kind="invert"
    placement="top"
    triggerRenderer={<Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicPrimary)} />}
  >
    Reach multiple recipients with your message by adding them to the loop.
  </Tooltip>
);

const PeopleInTheLoopPickerComponent: FC<IProps> = ({ requesterEmail, isMobile }) => {
  const form = useForm();
  const {
    input: {
      value: peopleInTheLoopInputValue,
      onChange: setPeopleInTheLoopInputValue,
      onBlur: peopleInTheLoopInputOnBlur,
    },
    meta: { error: peopleInTheLoopInputError },
  } = useField<string>("peopleInTheLoopInput");
  const {
    input: { value: addedPeopleInTheLoop, onChange: setAddedPeopleInTheLoop },
  } = useField<string[]>("peopleInTheLoop");

  const [inputVisible, setInputVisible] = useState(addedPeopleInTheLoop.length > 0);
  const addresses = useDebouncedAddresses("cc", peopleInTheLoopInputValue);
  const filteredAddresses = addresses.filter(
    address => !addedPeopleInTheLoop.includes(address) && address !== requesterEmail
  );

  const handleCloseInput = (): void => {
    setInputVisible(false);
    setPeopleInTheLoopInputValue("");
    setAddedPeopleInTheLoop([]);
  };

  const handleOpenInput = (): void => {
    setInputVisible(true);
  };

  const handleInputBlur = (): void => {
    peopleInTheLoopInputOnBlur();
    if (peopleInTheLoopInputError || !peopleInTheLoopInputValue) {
      return;
    }
    setAddedPeopleInTheLoop([...addedPeopleInTheLoop, peopleInTheLoopInputValue]);
    setPeopleInTheLoopInputValue("");
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      peopleInTheLoopInputOnBlur();
      if (peopleInTheLoopInputError) {
        return;
      }
      setAddedPeopleInTheLoop([...addedPeopleInTheLoop, peopleInTheLoopInputValue]);
      setPeopleInTheLoopInputValue("");
    }
  };

  const handleInputOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPeopleInTheLoopInputValue(e.target.value);
    form.resetFieldState("peopleInTheLoopInput");
  };

  return inputVisible ? (
    <>
      <Field
        name="peopleInTheLoopInput"
        validate={validatePeopleInTheLoopEmail(addedPeopleInTheLoop, requesterEmail)}
        render={({ input, meta: { touched, error } }) => (
          <FormField
            labelText="People in the loop"
            labelAdornment={
              <Block marginBottom="2px">
                <PeopleInTheLoopTooltip />
              </Block>
            }
            labelRightNode={
              <Button
                kind="plain"
                size="xcompact"
                icon={<Icon source={Close} />}
                onClick={handleCloseInput}
                data-testid="close-people-in-the-loop"
              />
            }
            error={touched && error}
            css={{
              marginBottom: withVar(
                isMobile || addedPeopleInTheLoop?.length > 0 ? SpacingToken.Spacing3 : SpacingToken.Spacing5
              ),
            }}
          >
            <AutoComplete
              placeholder="Enter email address and confirm with enter"
              error={touched && error}
              data-testid="input-people-in-the-loop"
              options={filteredAddresses}
              autoFocus
              data-1p-ignore
              onKeyDown={handleInputKeyDown}
              {...input}
              onChange={handleInputOnChange}
              onBlur={handleInputBlur}
            />
          </FormField>
        )}
      />
      {addedPeopleInTheLoop.length > 0 && (
        <Flex flexWrap gap="10px" marginBottom={withVar(isMobile ? SpacingToken.Spacing3 : SpacingToken.Spacing5)}>
          {addedPeopleInTheLoop.map(email => (
            <Tag
              key={email}
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onRemove={() => setAddedPeopleInTheLoop(addedPeopleInTheLoop.filter((e: any) => e !== email))}
              value={email}
              data-testid={`people-in-the-loop-tag-${email}`}
            />
          ))}
        </Flex>
      )}
    </>
  ) : (
    <Flex
      marginBottom={withVar(isMobile ? SpacingToken.Spacing3 : SpacingToken.Spacing5)}
      alignItems="center"
      gap={withVar(SpacingToken.Spacing2)}
    >
      <Button
        data-testid="add-people-in-the-loop"
        onClick={handleOpenInput}
        kind="link"
        size="compact"
        icon={<Icon source={Add} />}
      >
        Add people to the loop
      </Button>
      <PeopleInTheLoopTooltip />
    </Flex>
  );
};

export const PeopleInTheLoopPicker = withBreakpoints(PeopleInTheLoopPickerComponent);
